/**
 * Custom @GestionalePD Button styles
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';

/**
 * - onClick - click event handler
 * - children - Button name
 * - customStyles - Custom CSS style
 * - type - Button Type
 * - disabled - Button status
 * - id - Button ID
 * @param {Function} onClick
 * @param {String} children
 * @param {Object} customStyles
 * @param {String} type
 * @param {Boolean} disabled
 * @param {String} id
 * @returns Cutom Default Black Outlined Button UI
 */
export const DefaultOutlinedButton = ({
  onClick,
  component,
  children,
  customStyles,
  type,
  disabled,
  id,
  endIcon,
  startIcon,
  href,
}) => {
  const defaultStyles = {
    borderColor: '#e0e4e7',
    color: '#212b36',
    '&:hover': {
      borderColor: '#454f4b',
      boxShadow: '0px 0px 0px 0.5px',
      color: '#454f4b',
      backgroundColor: '#eeeeef',
    },
  };

  return (
    <Button
      href={href}
      component={component}
      variant="outlined"
      id={id}
      disabled={disabled}
      endIcon={endIcon}
      startIcon={startIcon}
      sx={{
        ...defaultStyles,
        ...customStyles,
      }}
      onClick={onClick}
      type={type}
    >
      {children}
    </Button>
  );
};

// Custom Default Outlined Button parameter type definition
DefaultOutlinedButton.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.string,
  customStyles: PropTypes.object,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

/**
 * - onClick - Click event handler
 * - children - Button name
 * - customStyles - Custom CSS styles
 * - type - Button type
 * - fullWidth - Button UI width
 * - endIcon - Button additional post icon
 * - disabled - Button status
 * - to - Button naviagtion path
 * - id - Button ID
 * - component - Button component
 * - autofocus - Button initial focus opton
 * - href - Button Navigation link
 * @param {Function} onClick
 * @param {String} children
 * @param {Object} customStyles
 * @param {String} type
 * @param {Boolean} fullWidth
 * @param {Component} endIcon
 * @param {Boolean} disabled
 * @param {String} to
 * @param {String} id
 * @param {Component} component
 * @param {Boolean} autoFocus
 * @param {String} href
 * @returns Custom Default Black Contained Button UI
 */
export const DefaultContainedButton = ({
  onClick,
  children,
  customStyles,
  type,
  fullWidth,
  endIcon,
  startIcon,
  disabled,
  to,
  id,
  component,
  autoFocus,
  href,
  colorVariant = 'default',
}) => {
  const colorStyles = {
    default: {
      backgroundColor: '#212b36',
      '&:hover': {
        backgroundColor: '#454f4b',
      },
    },
    blue: {
      backgroundColor: '#1a73e8',
      '&:hover': {
        backgroundColor: 'rgb(18, 80, 151)',
      },
    },
    red: {
      backgroundColor: 'red',
      '&:hover': {
        backgroundColor: '#b71c1c',
      },
    },
  };

  const defaultStyles = {
    boxShadow: 'none',
    ...colorStyles[colorVariant],
  };

  return (
    <Button
      fullWidth={fullWidth}
      id={id}
      type={type}
      variant="contained"
      sx={{
        ...defaultStyles,
        ...customStyles,
      }}
      onClick={onClick}
      endIcon={endIcon}
      startIcon={startIcon}
      href={href}
      to={to}
      disabled={disabled}
      component={component}
      autoFocus={autoFocus}
    >
      {children}
    </Button>
  );
};

// Custom Default Contained Button parameter type definition
DefaultContainedButton.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node,
  customStyles: PropTypes.object,
  type: PropTypes.string,
  fullWidth: PropTypes.bool,
  endIcon: PropTypes.node,
  disabled: PropTypes.bool,
  to: PropTypes.string,
  id: PropTypes.string,
  component: PropTypes.node,
  autoFocus: PropTypes.bool,
  href: PropTypes.string,
};

/**
 * - onClick - Click event handler
 * - children - Button name
 * - customStyles - Custom CSS styles
 * - type - Button type
 * - fullWidth - Button UI width
 * - endIcon - Button additional post icon
 * - disabled - Button status
 * - to - Button naviagtion path
 * - id - Button ID
 * - component - Button component
 * - autofocus - Button initial focus opton
 * - href - Button Navigation link
 * @param {Function} onClick
 * @param {String} children
 * @param {Object} customStyles
 * @param {String} type
 * @param {Boolean} fullWidth
 * @param {Component} endIcon
 * @param {Boolean} disabled
 * @param {String} to
 * @param {String} id
 * @param {Component} component
 * @param {Boolean} autoFocus
 * @param {String} href
 * @returns Custom Default Black Contained Button UI
 */
export const DefaultContainedLoadingButton = ({
  onClick,
  children,
  customStyles,
  type,
  fullWidth,
  endIcon,
  startIcon,
  disabled,
  to,
  id,
  component,
  autoFocus,
  loading,
  href,
}) => {
  const defaultStyles = {
    backgroundColor: '#212b36',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#454f4b',
    },
  };

  return (
    <LoadingButton
      fullWidth={fullWidth}
      loading={loading}
      id={id}
      type={type}
      variant="contained"
      sx={{
        ...defaultStyles,
        ...customStyles,
      }}
      onClick={onClick}
      endIcon={endIcon}
      startIcon={startIcon}
      href={href}
      to={to}
      disabled={disabled}
      component={component}
      autoFocus={autoFocus}
    >
      {children}
    </LoadingButton>
  );
};

// Custom Default Contained Button parameter type definition
DefaultContainedLoadingButton.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node,
  customStyles: PropTypes.object,
  type: PropTypes.string,
  fullWidth: PropTypes.bool,
  endIcon: PropTypes.node,
  disabled: PropTypes.bool,
  to: PropTypes.string,
  id: PropTypes.string,
  component: PropTypes.node,
  autoFocus: PropTypes.bool,
  href: PropTypes.string,
};

/**
 * - onClick - Button click event handler
 * - type - Button type
 * - children - Button name
 * - customStyles - Custom CSS styles
 * - disabled - Buttton status
 * @param {Function} onClick
 * @param {String} type
 * @param {String} children
 * @param {Object} customStyles
 * @param {Boolean} disabled
 * @returns Cutsom Red Outlined Button UI
 */
export const RedOutlinedButton = ({ onClick, type, children, customStyles, disabled }) => (
  <Button
    variant="outlined"
    color="error"
    type={type}
    disabled={disabled}
    sx={{
      ...customStyles,
    }}
    onClick={onClick}
  >
    {children}
  </Button>
);

// Custom Red Outlined Button parameter type definition
RedOutlinedButton.propTypes = {
  onClick: PropTypes.func,
  type: PropTypes.string,
  children: PropTypes.string,
  customStyles: PropTypes.object,
  disabled: PropTypes.bool,
};

export const RedOutlinedLoadingButton = ({ onClick, type, children, customStyles, disabled, loading }) => (
  <LoadingButton
    variant="outlined"
    color="error"
    loading={loading}
    type={type}
    disabled={disabled}
    sx={{
      ...customStyles,
    }}
    onClick={onClick}
  >
    {children}
  </LoadingButton>
);

// Custom Red Outlined Button parameter type definition
RedOutlinedLoadingButton.propTypes = {
  onClick: PropTypes.func,
  type: PropTypes.string,
  children: PropTypes.string,
  customStyles: PropTypes.object,
  disabled: PropTypes.bool,
};

/**
 * - onClick - Button click event handler
 * - children - Button name
 * - customStyles - Custom CSS styles
 * - startIcon - Button additional pre-icon
 * @param {Function} onClick
 * @param {String} children
 * @param {Object} customStyles
 * @param {Component} startIcon
 * @returns Custom Green Outlined Button UI
 */
export const GreenOutlinedButton = ({ onClick, children, customStyles, startIcon }) => (
  <Button
    variant="outlined"
    sx={{
      ...customStyles,
    }}
    startIcon={startIcon}
    onClick={onClick}
  >
    {children}
  </Button>
);

// Custom Green Outlined Button parameter type definition
GreenOutlinedButton.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.string,
  customStyles: PropTypes.object,
  startIcon: PropTypes.node,
};

export const CustomIconButton = ({ sx, children, onClick, size, disabled, disableRipple }) => {
  const defaultStyles = {
    '&:hover': {
      backgroundColor: '#d6f5e0',
    },
  };

  return (
    <IconButton
      sx={{
        ...defaultStyles,
        ...sx,
      }}
      disabled={disabled}
      disableRipple={disableRipple}
      size={size}
      onClick={onClick}
    >
      {children}
    </IconButton>
  );
};

export const SearchIconButton = ({ sx, children, onClick, size, disabled, disableRipple }) => {
  const defaultStyles = {
    border: '1px solid #e0e4e7',
    color: '#212b36',
    borderRadius: '6px',
    padding: '6px 18px',
    height: '40px',
    '&:hover': {
      backgroundColor: '#eeeeef',
      border: '1px solid black',
    },
  };

  return (
    <IconButton
      sx={{
        ...defaultStyles,
        ...sx,
      }}
      disabled={disabled}
      disableRipple={disableRipple}
      size={size}
      onClick={onClick}
    >
      {children}
    </IconButton>
  );
};
